import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Trash2, RotateCcw, UserX } from 'lucide-react';
import DeleteAccountModal from './DeleteAccountModal';
import { useGameState } from '@/contexts/GameStateContext';
import { useToast } from '@/hooks/use-sonner-toast';
import ConfirmationDialog from './ui/confirmation-dialog';
import { DeleteElementModal } from './DeleteElementModal';
import { Element as ElementType } from '@/types';

// Declare global type for the window object
declare global {
  interface Window {
    selectElementForDeletion?: (element: ElementType) => void;
    handleDeleteElementClick?: () => void;
  }
}

const DangerZoneMenu: React.FC = () => {
  const { resetElements } = useGameState();
  const { toast } = useToast();
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [deleteElementOpen, setDeleteElementOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<ElementType | null>(null);
  const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
  const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

  // Create a function to handle the delete element action
  const handleDeleteElement = () => {
    setDeleteElementOpen(true);

    // Create a "Delete Element Mode" similar to the reporting mode
    document.body.classList.add('delete-element-mode');

    // Set up a global handler for element selection
    window.selectElementForDeletion = (element: ElementType) => {
      setSelectedElement(element);
      document.body.classList.remove('delete-element-mode');
    };

    // Add a click handler to the document to capture clicks outside of elements
    const handleDocumentClick = (e: MouseEvent) => {
      // If the click is not on an element and not in the modal, cancel the delete mode
      const target = e.target as HTMLElement;

      // Check if the click is inside a dialog or element
      const isInDialog = target.closest('[role="dialog"]');
      const isOnElement = target.closest('.element');

      if (!isOnElement && !isInDialog) {
        setDeleteElementOpen(false);
        document.body.classList.remove('delete-element-mode');
        document.removeEventListener('click', handleDocumentClick);
      }
    };

    // Add the event listener
    setTimeout(() => {
      document.addEventListener('click', handleDocumentClick);
    }, 100);
  };

  // Handle closing the delete element modal
  const handleCloseDeleteModal = () => {
    setDeleteElementOpen(false);
    setSelectedElement(null);
    document.body.classList.remove('delete-element-mode');
  };

  // Reset Elements handlers
  const handleResetElements = () => {
    setShowFirstConfirmation(true);
  };

  const handleFirstConfirm = () => {
    setShowFirstConfirmation(false);
    setShowSecondConfirmation(true);
  };

  const handleSecondConfirm = () => {
    // Reset elements to just the starting 4
    resetElements();
    setShowSecondConfirmation(false);
    toast({
      title: 'Elements Reset',
      description: 'Your elements have been reset to the starting four elements.',
      variant: 'default',
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive dark:text-red-400 dark:hover:text-red-300"
          >
            <AlertTriangle className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Danger Zone</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Delete Element Button */}
          <DropdownMenuItem
            onClick={handleDeleteElement}
            className="text-destructive dark:text-red-400 cursor-pointer"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Element
          </DropdownMenuItem>

          {/* Reset Elements Button */}
          <DropdownMenuItem
            onClick={handleResetElements}
            className="text-destructive dark:text-red-400 cursor-pointer"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset Elements
          </DropdownMenuItem>

          {/* Delete Account Button */}
          <DropdownMenuItem
            onClick={() => setIsDeleteAccountModalOpen(true)}
            className="text-destructive dark:text-red-400 cursor-pointer"
          >
            <UserX className="mr-2 h-4 w-4" />
            Delete Account
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteAccountModal
        isOpen={isDeleteAccountModalOpen}
        onClose={() => setIsDeleteAccountModalOpen(false)}
      />

      {/* Delete Element Dialog */}
      {deleteElementOpen && (
        <>
          {/* Instruction banner at the top of the screen */}
          {!selectedElement && (
            <div className="fixed top-0 left-0 right-0 bg-destructive text-destructive-foreground py-3 px-4 z-50 flex items-center justify-between shadow-lg">
              <div className="flex items-center">
                <span className="font-medium">
                  Delete Element Mode: Click on any element in the Elements & Discoveries panel to
                  delete it.
                </span>
              </div>
              <Button variant="secondary" onClick={handleCloseDeleteModal} className="ml-4">
                Cancel
              </Button>
            </div>
          )}

          {/* Semi-transparent overlay for everything except Elements & Discoveries */}
          {!selectedElement && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 pointer-events-none">
              {/* This div is transparent and covers the entire screen */}
            </div>
          )}
        </>
      )}

      {/* Delete Element Modal */}
      <DeleteElementModal
        isOpen={!!selectedElement && deleteElementOpen}
        onClose={handleCloseDeleteModal}
        element={selectedElement}
      />

      {/* First confirmation dialog for Reset Elements */}
      <ConfirmationDialog
        open={showFirstConfirmation}
        onOpenChange={setShowFirstConfirmation}
        title="Reset Elements & Discoveries?"
        description="This will remove all your elements and discoveries except for the starting four elements (Water, Fire, Dirt, Air). This action cannot be undone."
        confirmText="Continue"
        cancelText="Cancel"
        onConfirm={handleFirstConfirm}
        variant="destructive"
      />

      {/* Second confirmation dialog for Reset Elements */}
      <ConfirmationDialog
        open={showSecondConfirmation}
        onOpenChange={setShowSecondConfirmation}
        title="Are you REALLY sure?"
        description="You will lose ALL your discoveries and elements except the starting four. This cannot be undone or recovered!"
        confirmText="Yes, Reset Everything"
        cancelText="No, Keep My Elements"
        onConfirm={handleSecondConfirm}
        variant="destructive"
      />
    </>
  );
};

export default DangerZoneMenu;
